/**
 * Overview tab — the customer at a glance: a slim set of money-free KPIs (utilization band, meetings/30d,
 * seats, and the renewal DATE + countdown — NOT a renewal amount), plus the MIRROR freshness badge (data
 * age, from last_synced_at) and the mirror-health badge. By design Overview shows NO money: every money
 * figure lives on the Invoice tab (see InvoicesTab), the only surface that formats currency.
 *
 * The Sync-now control (plans-usage) lives on the Subscription tab, not here; its LIVE "Fetched live at …"
 * badge is a deliberately separate signal from this tab's mirror "Last Update: …" badge — the sync-now
 * live_at is never written back to last_synced_at, so the two timestamps never share a value.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FreshnessBadge } from "@/components/portal/FreshnessBadge";
import { AccountStatusBadge } from "@/components/portal/AccountStatusBadge";
import { PlanFreezeNotice } from "@/components/portal/PlanFreezeNotice";
import { SkeletonRows, EmptyState, ErrorState, Skeleton } from "@/components/ui";
import { formatDate, formatInt } from "@/lib/format";
import { isPlanNotLive } from "@/lib/planState";
import { isZombieFreeAccount, showsRenewal } from "@/lib/accountStatus";
import type { CustomerOverview } from "@/api/types";
import {
  KpiTile,
  Section,
  TierBadge,
  UtilizationBar,
  utilizationTone,
  bandFromLabel,
} from "../display";
import { useCustomerOverview, useMirrorHealth } from "../useCustomer";

export function OverviewTab({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const overview = useCustomerOverview(accountId);
  const mirrorHealth = useMirrorHealth();

  if (overview.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {/* r10 22/7: auto-fit KPI tiles — no fixed column cap now the shell is full-bleed. */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <SkeletonRows rows={2} />
      </div>
    );
  }

  if (overview.isError) {
    return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  }

  const d = overview.data;
  // EXACTLY false — never a truthiness test. `undefined` (a payload that doesn't carry the flag) must
  // mean "not confirmed retired" and render the page normally, matching RetiredBadge's fail-safe rule.
  const isRetired = d.is_active === false;

  return (
    <div className="flex flex-col gap-6">
      <Section
        title={t("customer.account")}
        trailing={<TierBadge tier={d.tier ?? null} tierKnown={d.tier_known ?? false} />}
      >
        <dl className="grid gap-x-6 gap-y-3 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm) sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t("customer.accountName")} value={d.account_name ?? "—"} />
          <Field label={t("customer.accountNumber")} value={d.account_number ?? "—"} mono />
          <Field label={t("customer.owner")} value={d.owner_email ?? "—"} />
          {/* OWNER RULING (2026-09-18, UACJ 7045773031): this card used to print the real plan SKU here
              (planName(d.base_type) — "Zoom Workplace Enterprise Annual" for that account) under the
              "Plan" label. The owner ruled that wrong: this card must show ACCOUNT TYPE, the same
              account_type_display the header already carries (N0-4) — DISPLAY ONLY, Zoom's own "Account
              Type" as its Customer List prints it, with the Enterprise correction baked in server-side
              (see _display_account_type in customers/service.py). One rendering rule for account_type_display,
              not two: same i18n key (customer.accountType) and the same "omit when absent" fallback as
              CustomerHeader, so an older API payload with no account_type_display shows no Account type
              fact here either, instead of a raw decoy or a blank label. Nothing may branch on this value —
              base_type still drives Plan status, the TierBadge, and PlanFreezeNotice below, unchanged. */}
          {d.account_type_display && (
            <Field label={t("customer.accountType")} value={d.account_type_display} />
          )}
          {/* One simple status (Active / Free / Expired / Unknown), same derivation as the header badge.
              No base-plan row → "Free" (not an em-dash, not a false "expired"); an opaque code → "Unknown". */}
          <Field
            label={t("customer.planStatus")}
            value={
              <AccountStatusBadge
                plan_state={d.plan_state}
                base_type={d.base_type}
                plan_badge={d.plan_badge}
              />
            }
          />
        </dl>
      </Section>

      <Section
        title={t("customer.tabs.overview")}
        trailing={
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {/* (1) mirror data-age — NOT the live badge, NOT the health badge. `nightly` is hardcoded
                because last_synced_at is the NIGHTLY ingest marker: sync-now NEVER writes it back to
                the mirror (it returns a separate live_at; see syncnow/service.py — ok sets live_at,
                never touches last_synced_at). If a future backend ever folds a live fetch into the
                mirror, derive this from the payload instead, or it would mislabel live data as nightly. */}
            <FreshnessBadge kind="mirror" timestamp={d.last_synced_at} nightly />
            {/* (2) mirror-health — a SEPARATE question (is the POLLER current?), and a GLOBAL one:
                useMirrorHealth()'s queryKey carries no account id. On a RETIRED account that makes it
                actively misleading — it renders a green "Mirror is current" directly above KPIs that
                have been frozen since the day Zoom stopped listing the account, and a CSM reasonably
                reads it as "this account's data is current". The poller's health says nothing about an
                account it no longer receives data for, so we suppress it here and let the freeze notice
                below answer the freshness question truthfully instead. */}
            {!isRetired && mirrorHealth.data && (
              <FreshnessBadge
                kind="mirror-health"
                healthy={mirrorHealth.data.healthy}
                checkedAt={mirrorHealth.data.checked_at}
              />
            )}
          </div>
        }
      >
        {/* The badge in the header says the account was DELISTED. That fact has to travel to the numbers:
            ZPCD stops refreshing an account the moment it drops off Zoom's list, so every KPI below —
            utilization, renewal countdown, seats, meetings/30d — is a pre-retirement snapshot that will
            never update again. Without this line the tiles render with completely normal, healthy styling
            and get quoted as live data (e.g. "76% utilization, renewal in 53 days") for an account that is
            no longer a Zoom customer. */}
        {isRetired && (
          <p className="mb-3 rounded-(--radius-sm) border border-(--warn) bg-(--warn-subtle) px-3 py-2 text-(length:--text-sm) text-(--text)">
            {d.left_list_at
              ? t("retired.frozenSince", { date: formatDate(d.left_list_at) })
              : t("retired.frozen")}
          </p>
        )}
        {/* A SECOND, INDEPENDENT freeze — the PLAN is dead, which is not the same fact as "Zoom stopped
            listing the account". The KPIs below (utilization, seats) come from /plans/usage, which goes
            SILENT for a dead plan, so they are frozen even on an account Zoom still happily lists. When an
            account is BOTH retired and plan-expired, both notices render: they answer different questions,
            and dropping either loses a fact. */}
        <PlanFreezeNotice
          plan_state={d.plan_state}
          plan_badge={d.plan_badge}
          base_type={d.base_type}
          className="mb-3"
        />
        {/* NO INCIDENT BANNER, and NO special badge either — by design (2026-07-15). A `workaround_trial`
            account (paid, renewed, wrongly expired by Zoom, then patched onto a temporary plan) once grew a
            six-sentence danger banner AND a distinct ⚠️ "Open incident" badge here. Both are gone: the
            seven-way plan_state badge collapsed to the four-value account status (lib/accountStatus.ts), and
            คุณใหม่ chose to map workaround_trial to a plain "Active" pill (its temporary plan is genuinely
            live). The portal no longer flags the open-Zoom-incident at all — that detail lives ONLY in the
            backend v_plan_incident view / the desktop deliverable now, not anywhere in this UI. Do not
            re-add a portal-side incident signal without re-asking; it was considered and declined. */}
        <Kpis data={d} />
      </Section>
    </div>
  );
}

function Kpis({ data }: { data: CustomerOverview }) {
  const { t } = useTranslation();
  const renewalSub =
    data.days_until_renewal != null
      ? t("customer.daysUntilRenewal", { days: data.days_until_renewal })
      : undefined;

  // ZPCD's utilization_band is prose with a parenthetical hint (e.g. "High (Upsell)"). Normalise it ONCE
  // to a canonical band and drive BOTH the localized label and the bar tone from it, so the label and
  // the colour can never disagree (the old code fed raw prose to the tone → every account read green).
  const bandCode = bandFromLabel(data.utilization_band);

  // Only relabel the tile when the plan is dead AND we actually still hold the historical figure. If the
  // backend gave us neither, fall through to the normal tile, which renders a plain "—". Never advertise
  // a "last known" number we do not have.
  const seatsFrozen = isPlanNotLive(data.plan_state) && data.seats_last_known != null;

  // ⚠️ ZOMBIE-ROW SUPPRESSION (API 0030, round 2 W2) — the SAME gate CustomerHeader.tsx applies to its own
  // seats/renewal facts, shared via lib/accountStatus.ts::isZombieFreeAccount so the two surfaces can
  // never disagree. A `free` verdict that OVERRODE a real plan_state/base_type reading (QUALICA, G STEEL,
  // GJ Steel) means the Renewal tile and the Seats / "Seats (last known)" tile below are leftovers of a
  // plan the ruling says does not apply — the header already hides them; this tab's OWN copies of the
  // same numbers must too, or a "Free" pill one section up sits beside a current-looking "Seats: 50" one
  // section down. A DIFFERENT, untouched axis from `seatsFrozen` above and from isPlanNotLive /
  // PlanFreezeNotice elsewhere on this tab (open product question, see CustomerHeader.tsx's comment) —
  // this gate only decides whether the tile renders AT ALL, never how it is labelled.
  const isZombieFree = isZombieFreeAccount(data.plan_state, data.base_type, data.plan_badge);
  const renewalShown = showsRenewal(data.plan_state, data.base_type, data.plan_badge);

  return (
    // r10 22/7: auto-fit KPI tiles — no fixed column cap now the shell is full-bleed.
    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
      <KpiTile
        label={t("customer.utilization")}
        mono={false}
        value={data.utilization_band ? t(`utilizationBand.${bandCode}`) : "—"}
        sub={
          <UtilizationBar
            pct={data.plan_util_pct}
            tone={utilizationTone(bandCode)}
            className="mt-1 w-40"
          />
        }
      />
      <KpiTile label={t("customer.meetings30d")} value={formatInt(data.meetings_30d)} />
      {/* W8-Q1 (owner ruling 2026-09-19): no Renewal tile at all on a Free account — Zoom prints no
          dates for "Basic Plan (Free)". The SAME gate as the header's Renewal fact (showsRenewal). */}
      {renewalShown && (
        <KpiTile
          label={t("customer.renewal")}
          value={formatDate(data.renewal_date)}
          {...(renewalSub ? { sub: renewalSub } : {})}
        />
      )}
      {/* The seats tile. `seats` is NULL once the plan is provably dead (the backend moves the frozen
          figure to seats_last_known), so this tile would otherwise just render "—" with no explanation —
          and an unexplained em-dash is its own small lie when we DO still know the number. Relabel it
          "Seats (last known)" and keep the figure, clearly marked as historical.
          The LABEL is the whole warning. It used to carry a `sub` paragraph underneath repeating, in
          longer words, that the number is not current — a footnote to a label that already said so. Cut.
          `!data.no_meeting_plan` (N0-4): SAME rule CustomerHeader.tsx applies to its own Seats facts — a
          Zoom-Phone-only account's seat figure is a 0 default, never a size, so "Seats (last known) 0"
          said nothing true. Was missing here: the header hid the tile, this tab kept showing the zero. */}
      {!isZombieFree &&
        !data.no_meeting_plan &&
        (seatsFrozen ? (
          <KpiTile
            label={t("planState.seatsLastKnown")}
            value={formatInt(data.seats_last_known ?? null)}
          />
        ) : (
          <KpiTile label={t("customer.seats")} value={formatInt(data.seats)} />
        ))}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-(length:--text-2xs) font-medium text-(--text-muted)">{label}</dt>
      <dd
        className={
          mono
            ? "tabular truncate text-(length:--text-sm) text-(--text-strong)"
            : "truncate text-(length:--text-sm) text-(--text)"
        }
      >
        {value}
      </dd>
    </div>
  );
}

/** Tiny re-export so the tab module can show a clean empty when an account truly has no overview. */
export function OverviewEmpty() {
  const { t } = useTranslation();
  return <EmptyState title={t("state.emptyTitle")} body={t("state.emptyBody")} />;
}
